"use client"
import { useState, useEffect } from "react"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import styles from "./contact-area.module.scss"
import Image from "next/image"
import StyledText from "src/typography/typography-component"
import skColors from "styles/skColors"
import axios from "axios"
import { toast } from "react-toastify"
import { useTranslations } from "next-intl"
import { sendMail } from "../../utils/sendMail"

const ContactArea = () => {
  const t = useTranslations("index.contactarea")
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = () => setIsExpanded((prev) => !prev)

  const contactValidationSchema = Yup.object().shape({
    name: Yup.string().required(t("validation.nameRequired") as string),
    email: Yup.string()
      .email(t("validation.emailInvalid") as string)
      .required(t("validation.emailRequired") as string),
    subject: Yup.string().required(t("validation.subjectRequired") as string),
    message: Yup.string().required(t("validation.messageRequired") as string),
  })
  useEffect(() => {
    if (typeof window !== "undefined") {
      const shouldOpen = sessionStorage.getItem("openContact")

      if (shouldOpen === "true") {
        setIsExpanded(true)

        sessionStorage.removeItem("openContact")

        setTimeout(() => {
          const el = document.getElementById("contact-form")
          el?.scrollIntoView({ behavior: "smooth" })

          // ✅ REMOVE HASH after scroll
          window.history.replaceState(null, "", window.location.pathname)
        }, 100)
      }
    }
  }, [])
  return (
    <div id="contact-form" className={styles.contactContainer} role="button" aria-expanded={isExpanded} tabIndex={0}>
      {/* Header row: Kontakt + toggle */}
      <div onClick={toggleExpanded} className={styles.headerRow}>
        <StyledText variant="h2" className={styles.kontakt} color={skColors.turquoise[600]}>
          {t("title")}
        </StyledText>
        <button
          className={styles.toggleButton}
          onClick={(e) => {
            e.stopPropagation()
            toggleExpanded()
          }}
          aria-label={t("toggleAlt")}
        >
          <Image
            src="/svg/chevron_down.svg"
            alt={t("toggleAlt")}
            width={16}
            height={16}
            className={`${isExpanded ? styles.rotated : ""}`}
          />
        </button>
      </div>

      {/* Content row: image and text */}
      <div
        onClick={toggleExpanded} // whole container is clickable
        className={styles.contentRow}
      >
        <div className={styles.emailIcon}>
          <Image src="/svg/mail_image.svg" alt={t("emailImageAlt")} width={300} height={206} />
        </div>
        <div className={styles.wirFreuenUnsWrapper}>
          <StyledText variant="pRegular" className={styles.wirFreuenUns} color={skColors.darkgreen[600]}>
            {t("subtitle")}
          </StyledText>
        </div>
      </div>

      {/* Expanded section: form and contact info */}
      {isExpanded && (
        <div className={styles.expandedSection} onClick={(e) => e.stopPropagation()}>
          <div className={styles.formContainer}>
            <Formik
              initialValues={{
                name: "",
                email: "",
                subject: "",
                message: "",
              }}
              validationSchema={contactValidationSchema}
              onSubmit={(values, { resetForm, setSubmitting }) => {
                sendMail(
                  values,
                  () => {
                    resetForm(), setSubmitting(false)
                  },
                  t
                )
              }}
            >
              {({ errors, touched }) => (
                <Form className={styles.form}>
                  {/* Name + Email */}
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <StyledText variant="captions" color={skColors.darkgreen[500]} className={styles.labelName}>
                        {t("form.nameLabel")}
                      </StyledText>
                      <Field
                        name="name"
                        aria-invalid={touched.name && !!errors.name}
                        aria-describedby="err-name"
                        className={`${styles.input} ${touched.name && errors.name ? styles.inputError : ""}`}
                      />
                      {touched.name && errors.name && (
                        <div id="err-name" className={styles.error}>
                          {errors.name}
                        </div>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <StyledText variant="captions" color={skColors.darkgreen[500]} className={styles.labelName}>
                        {t("form.emailLabel")}
                      </StyledText>
                      <Field
                        name="email"
                        aria-invalid={touched.email && !!errors.email}
                        aria-describedby="err-email"
                        className={`${styles.input} ${touched.email && errors.email ? styles.inputError : ""}`}
                      />
                      {touched.email && errors.email && (
                        <div id="err-email" className={styles.error}>
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <StyledText variant="captions" color={skColors.darkgreen[500]} className={styles.labelName}>
                      {t("form.subjectLabel")}
                    </StyledText>
                    <Field
                      name="subject"
                      aria-invalid={touched.subject && !!errors.subject}
                      aria-describedby="err-subject"
                      className={`${styles.input} ${touched.subject && errors.subject ? styles.inputError : ""}`}
                    />
                    {touched.subject && errors.subject && (
                      <div id="err-subject" className={styles.error}>
                        {errors.subject}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <StyledText variant="captions" color={skColors.darkgreen[500]} className={styles.labelName}>
                      {t("form.messageLabel")}
                    </StyledText>
                    <Field
                      name="message"
                      as="textarea"
                      rows={4}
                      aria-invalid={touched.message && !!errors.message}
                      aria-describedby="err-message"
                      className={`${styles.textarea} ${touched.message && errors.message ? styles.textareaError : ""}`}
                    />
                    {touched.message && errors.message && (
                      <div id="err-message" className={styles.error}>
                        {errors.message}
                      </div>
                    )}
                  </div>

                  <button type="submit" className={styles.submitButton}>
                    <StyledText variant="h6" color="white">
                      {t("form.submit")}
                    </StyledText>
                  </button>
                </Form>
              )}
            </Formik>
          </div>

          {/* Contact info section */}
          <div className={styles.infoContainer}>
            <div className={styles.infoWrapper}>
              <StyledText variant="captions" className={styles.heading} color={skColors.darkgreen[600]}>
                {t("address.heading")}
              </StyledText>
              <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                {t("address.line1")}
              </StyledText>
              <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                {t("address.line2")}
              </StyledText>
              <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                {t("address.line3")}
              </StyledText>
              <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                {t("address.line4")}
              </StyledText>
            </div>

            <div className={styles.infoWrapper}>
              <StyledText variant="captions" className={styles.heading} color={skColors.darkgreen[600]}>
                {t("service.heading")}
              </StyledText>
              <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                {t("service.line1")}
              </StyledText>
              <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                {t("service.line2")}
              </StyledText>
              <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                {t("service.line3")}
              </StyledText>
            </div>

            <div className={styles.infoWrapper}>
              <StyledText variant="captions" className={styles.heading} color={skColors.darkgreen[600]}>
                {t("contact.heading")}
              </StyledText>
              <a href={`tel:${t("contact.phoneNumberLink")}`}>
                <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                  {t("contact.phoneLabel")} {t("contact.phoneNumber")}
                </StyledText>
              </a>
              <StyledText variant="pSmall" style={{ display: "inline" }}>
                <span style={{ color: skColors.darkgreen[500] }}>{t("contact.emailLabel")}</span>
                <span
                  style={{
                    color: skColors.turquoise[500],
                    cursor: "pointer",
                    marginLeft: "8px",
                  }}
                  onClick={() => (window.location.href = `mailto:${t("contact.email")}`)}
                >
                  {t("contact.email")}
                </span>
              </StyledText>
            </div>

            <div className={styles.infoWrapper}>
              <StyledText
                variant="pSmall"
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {t("support.intro")}
                <span
                  style={{
                    color: skColors.turquoise[500],
                    cursor: "pointer",
                    marginRight: "8px",
                  }}
                  onClick={() => (window.location.href = `mailto:${t("support.email")}`)}
                >
                  {t("support.email")}
                </span>
                <a href={`tel:${t("support.phoneLink")}`}>
                  <StyledText variant="pSmall" className={styles.text} color={skColors.darkgreen[500]}>
                    {t("support.phone")}
                  </StyledText>
                </a>
              </StyledText>
            </div>

            <div className={styles.infoWrapper}>
              <StyledText variant="captions" className={styles.heading}>
                {t("follow.heading")}
              </StyledText>
              <div className={styles.socialIcons}>
                <a href="https://www.instagram.com/skinuvita" target="_blank" rel="noopener noreferrer">
                  <Image src="/svg/instagram.svg" alt="Instagram" width={32} height={32} />
                </a>

                <a href="https://www.linkedin.com/company/skinuvita" target="_blank" rel="noopener noreferrer">
                  <Image src="/svg/linkedin.svg" alt="LinkedIn" width={32} height={32} />
                </a>

                <a href="https://wa.me/4942149915300" target="_blank" rel="noopener noreferrer">
                  <Image src="/svg/whatsapp.svg" alt="WhatsApp" width={32} height={32} />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContactArea
